'use client'

import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useId, useRef, useState } from 'react'

import { GoogleNewsGlyph } from '@/components/Blog/GoogleNewsGlyph'
import { XLogoIcon } from '@/components/icons/XLogoIcon'

import './post-share-bar.css'

/** Toggles + texto custom do Google News, resolvidos no servidor (global singlePostOptions). */
export type ShareBarConfig = {
  showX: boolean
  showTelegram: boolean
  showWhatsapp: boolean
  showCopyLink: boolean
  showGoogleNews: boolean
  /** Texto custom do botão Google News; vazio = usa o i18n padrão. */
  googleNewsLabel?: string
}

type Props = {
  articleUrl: string
  articleTitle: string
  /** URL da publicação no Google News ou pesquisa; sempre definida no servidor. */
  googleNewsUrl: string
  /** Quais botões exibir + texto do Google News. */
  share: ShareBarConfig
}

function ShareTriggerIcon() {
  return (
    <svg
      className="post-share-icon"
      xmlns="http://www.w3.org/2000/svg"
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  )
}

function IconWhatsapp() {
  return (
    <svg
      className="post-share-dropdown-icon"
      xmlns="http://www.w3.org/2000/svg"
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  )
}

function IconTelegram() {
  return (
    <svg
      className="post-share-dropdown-icon"
      xmlns="http://www.w3.org/2000/svg"
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  )
}

function IconCopy() {
  return (
    <svg
      className="post-share-dropdown-icon"
      xmlns="http://www.w3.org/2000/svg"
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  )
}

function buildShareLinks(url: string, title: string) {
  const u = encodeURIComponent(url)
  const t = encodeURIComponent(title)
  return {
    x: `https://x.com/intent/tweet?text=${t}&url=${u}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${title} ${url}`)}`,
    telegram: `https://t.me/share/url?url=${u}&text=${t}`,
  }
}

/**
 * Barra de partilha (dropdown + Google News) após a imagem de destaque — design alinhado ao velociraptor.
 */
export function PostHeroShareBar({ articleUrl, articleTitle, googleNewsUrl, share }: Props) {
  const t = useTranslations('Article')
  const reactId = useId()
  const safeId = reactId.replace(/:/g, '')
  const dropdownId = `post-share-dropdown-${safeId}`
  const wrapRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [copyLabel, setCopyLabel] = useState(t('copyLink'))

  const links = buildShareLinks(articleUrl, articleTitle)
  const anyDropdown =
    share.showX || share.showTelegram || share.showWhatsapp || share.showCopyLink
  const gnLabel = share.googleNewsLabel?.trim() || t('readOnGoogleNews')

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close()
    }
    document.addEventListener('click', onDoc)
    return () => document.removeEventListener('click', onDoc)
  }, [open, close])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, close])

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(articleUrl)
      setCopyLabel(t('copied'))
      window.setTimeout(() => setCopyLabel(t('copyLink')), 2000)
    } catch {
      /* ignorar */
    }
  }

  // Nada para exibir (todos os botões desligados) → não renderiza a barra vazia.
  if (!anyDropdown && !share.showGoogleNews) return null

  return (
    <div className="post-share-bar" data-test="post-hero-share-bar">
      <div className="post-share-bar-inner">
        {anyDropdown ? (
        <div className="post-share-bar-group post-share-dropdown-wrap" ref={wrapRef}>
          <button
            type="button"
            className="post-share-btn"
            aria-label={t('share')}
            aria-expanded={open}
            aria-haspopup="true"
            aria-controls={dropdownId}
            data-post-share-trigger=""
            onClick={(e) => {
              e.stopPropagation()
              setOpen((v) => !v)
            }}
          >
            <ShareTriggerIcon />
            <span className="post-share-btn-text">{t('share')}</span>
          </button>
          <div
            id={dropdownId}
            className="post-share-dropdown"
            role="menu"
            hidden={!open}
          >
            {share.showX ? (
              <a
                href={links.x}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="post-share-dropdown-item"
                role="menuitem"
              >
                <XLogoIcon className="post-share-dropdown-icon" />
                <span className="post-share-dropdown-label">X</span>
              </a>
            ) : null}
            {share.showTelegram ? (
              <a
                href={links.telegram}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="post-share-dropdown-item"
                role="menuitem"
              >
                <IconTelegram />
                <span className="post-share-dropdown-label">Telegram</span>
              </a>
            ) : null}
            {share.showWhatsapp ? (
              <a
                href={links.whatsapp}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="post-share-dropdown-item"
                role="menuitem"
              >
                <IconWhatsapp />
                <span className="post-share-dropdown-label">WhatsApp</span>
              </a>
            ) : null}
            {share.showCopyLink ? (
              <button
                type="button"
                className="post-share-dropdown-item"
                role="menuitem"
                aria-label={t('copyLink')}
                onClick={() => {
                  void copyLink()
                }}
              >
                <IconCopy />
                <span className="post-share-copy-text">{copyLabel}</span>
              </button>
            ) : null}
          </div>
        </div>
        ) : null}

        {share.showGoogleNews ? (
          <a
            href={googleNewsUrl}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="post-share-btn post-share-google-news-btn"
            aria-label={gnLabel}
          >
            <GoogleNewsGlyph className="post-share-icon" />
            <span className="post-share-btn-text">{gnLabel}</span>
          </a>
        ) : null}

      </div>
      <div className="post-share-bar-sep" aria-hidden />
    </div>
  )
}
